import type {CompRef, Pointer, Pointers, Rect} from '@wix/document-services-types'
import {type DAL, type ExtensionAPI, pointerUtils} from '@wix/document-manager-core'
import type {VariantsExtensionAPI} from '../../variants/variants'
import type {ConversionDataBuilder, PartialConversionDataBuilder} from './types'
import type {DataModelExtensionAPI} from '../../dataModel/dataModel'
import type {ComponentConversionData, ConversionAlgoConfig} from '../types'
import {DATA_TYPES} from '../../../constants/constants'
import _ from 'lodash'
import {createAxisAlignedEnvelope} from './utils/axisAlignedEnvelope'
import {ConversionDalNamespaces} from '../conversionDal/constants'
import type {ThemeExtAPI} from '../../theme/theme'
import {getSkinDefinition} from '../../theme/defaultValues'
import type {FontStyleEntryItems, StyleEntryItem, TextAlignStyleEntryItems} from '../conversionDal/types'
import type {ViewerExtensionAPI} from '../../viewer/viewerExtension'
import {CENTER, JUSTIFY, LEFT} from '../plugins/alignment/constants'
import type {BuilderComponentsExtensionAPI} from '../../builderComponents/builderComponents'
import type {StructureExtensionAPI} from '../../structure'
import type {ComponentAlignment} from '../plugins/alignment/types'
import {AlgorithmModes} from '../enums'
// eslint-disable-next-line wix-editor/no-internal-import
import {STYLE} from '@wix/component-protocol/schema'
import type {SchemaExtensionAPI} from '../../schema/schema'
import * as constants from '../../../constants/constants'
import {builderTypes} from '../../../utils/builderUtils'

export const enum SemanticTypes {
    TEXT = 'TEXT',
    BUTTON = 'BUTTON',
    IMAGE = 'IMAGE',
    SVG = 'SVG',
    HORIZONTAL_LINE = 'HORIZONTAL LINE',
    VERTICAL_LINE = 'VERTICAL LINE',
    BOX = 'BOX',
    SECTION = 'SECTION',
    EMBEDDED_MEDIA = 'EMBEDDED MEDIA',
    UNCLASSIFIED = 'UNCLASSIFIED'
}

interface BuilderFontSizeData {
    style?: FontStyleEntryItems
    innerElementsStyle?: FontStyleEntryItems
}
interface SemanticTypeResolvers {
    predicate(componentType: string, type?: string): boolean
    semanticType: SemanticTypes
}

const TEXT_COMP_TYPES = new Set([
    'wysiwyg.viewer.components.WRichText',
    'wixui.CollapsibleText',
    'RichText',
    'CollapsibleText',
    'RichTextEditor',
    'Text'
])
const BUTTON_COMP_TYPES = new Set(['wysiwyg.viewer.components.SiteButton', 'Button', 'wixui.StylableButton'])
const IMAGE_COMP_TYPES = new Set(['wixui.ImageX', 'wysiwyg.viewer.components.WPhoto', 'Image'])
const SVG_COMP_TYPES = new Set(['wysiwyg.viewer.components.VectorImage'])
const MEDIA_CONTAINER_COMP_TYPE = new Set([
    'wysiwyg.viewer.components.MediaBox',
    'wysiwyg.viewer.components.StripColumnsContainer',
    'wysiwyg.viewer.components.ClassicSection',
    'wysiwyg.viewer.components.Column',
    'wysiwyg.viewer.components.StateBoxState',
    'wysiwyg.viewer.components.StateBoxFormState',
    'wysiwyg.viewer.components.StateStripState',
    'wysiwyg.viewer.components.BoxSlideShowSlide',
    'wysiwyg.viewer.components.StripContainerSlideShowSlide',
    'wysiwyg.viewer.components.MediaContainer',
    'wysiwyg.viewer.components.MenuContainer'
])
const EMBEDDED_MEDIA_COMP_TYPES = new Set([
    'wysiwyg.viewer.components.MediaPlayer',
    'wysiwyg.viewer.components.HtmlComponent',
    'wixui.VideoPlayer',
    'wixui.CustomElementComponent',
    'wysiwyg.viewer.components.GoogleMap',
    'GoogleMap',
    'VideoPlayer'
])
const HORIZONTAL_LINE_COMP_TYPE = new Set(['wysiwyg.viewer.components.FiveGridLine', 'Line'])
const VERTICAL_LINE_COMP_TYPE = 'wysiwyg.viewer.components.VerticalLine'
const SECTION_COMP_TYPES = new Set(['responsive.components.Section', 'wysiwyg.viewer.components.ClassicSection'])
const COMP_TYPES_TO_SEMANTIC_TYPE_MAPPER: SemanticTypeResolvers[] = [
    {
        predicate: (componentType: string) => TEXT_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.TEXT
    },
    {
        predicate: (componentType: string) => BUTTON_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.BUTTON
    },
    {
        predicate: (componentType: string) => IMAGE_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.IMAGE
    },
    {
        predicate: (componentType: string) => SVG_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.SVG
    },
    {
        predicate: (componentType: string) => EMBEDDED_MEDIA_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.EMBEDDED_MEDIA
    },
    {
        predicate: (componentType: string) => HORIZONTAL_LINE_COMP_TYPE.has(componentType),
        semanticType: SemanticTypes.HORIZONTAL_LINE
    },
    {
        predicate: (componentType: string) => componentType === VERTICAL_LINE_COMP_TYPE,
        semanticType: SemanticTypes.VERTICAL_LINE
    },
    {
        predicate: (componentType: string) => SECTION_COMP_TYPES.has(componentType),
        semanticType: SemanticTypes.SECTION
    },
    {
        predicate: (componentType: string, type: string) => type === 'Container',
        semanticType: SemanticTypes.BOX
    }
]

const getSemanticType = (componentTypeOrArchType: string, type: string): SemanticTypes => {
    for (const {predicate, semanticType} of COMP_TYPES_TO_SEMANTIC_TYPE_MAPPER) {
        if (predicate(componentTypeOrArchType, type)) {
            return semanticType
        }
    }

    return SemanticTypes.UNCLASSIFIED
}

export const createEnvelope = (rect: Rect, degrees: number | undefined): Rect => {
    if (degrees && degrees !== 0) {
        return createAxisAlignedEnvelope(rect, degrees)
    }
    return {...rect}
}

const commonBuilder: ConversionDataBuilder = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
): Omit<ComponentConversionData, 'virtualParentId'> => {
    const component = dal.get(compPointer)
    const {variants} = extensionAPI as VariantsExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {viewer} = extensionAPI as ViewerExtensionAPI
    const sourceLayout = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.layout)
    const targetLayout =
        conversionAlgoConfig.mode === AlgorithmModes.Adaptive
            ? variants.getComponentItemConsideringVariants(
                  pointers.components.getComponentVariantsPointer(compPointer, conversionAlgoConfig.targetVariants),
                  DATA_TYPES.layout
              )
            : undefined
    const transformations = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.transformations)
    const measurements = _.pick(viewer?.getComponentMeasurements(compPointer), ['x', 'y', 'width', 'height']) as Rect

    const conversionData = {
        [ConversionDalNamespaces.structure]: {
            id: compPointer.id,
            componentType: component.componentType,
            semanticType: getSemanticType(component.componentType, component.type),
            type: component.type!,
            skin: component.skin!,
            components: component.components,
            parent: component.parent!,
            axisAlignedEnvelope: createEnvelope(measurements, 0),
            data: dataModel.components.getItem(compPointer, DATA_TYPES.data),
            media: {
                uri: ''
            }
        },
        [ConversionDalNamespaces.measurements]: measurements,
        [ConversionDalNamespaces.sourceLayout]: sourceLayout,
        [ConversionDalNamespaces.targetLayout]: targetLayout
    }
    if (transformations?.rotate)
        conversionData[ConversionDalNamespaces.transformations] = {rotate: transformations.rotate}
    return conversionData
}

const getBuilderFontSize = (extensionAPI: ExtensionAPI, compPointer: CompRef): BuilderFontSizeData => {
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    const styleItems = builderComponents.getStyle(compPointer, true, [
        STYLE.CSS_PROPERTY_TYPE.font,
        STYLE.CSS_PROPERTY_TYPE.fontSize
    ])

    return styleItems.reduce<BuilderFontSizeData>((fontSizeData, styleData) => {
        const {value, prop, path, whereToWriteInDM} = styleData

        if (path) {
            if (!fontSizeData.innerElementsStyle) {
                fontSizeData.innerElementsStyle = []
            }
            fontSizeData.innerElementsStyle.push({data: value, prop, path, whereToWriteInDM})
        } else {
            if (!fontSizeData.style) {
                fontSizeData.style = []
            }
            fontSizeData.style.push({data: value, prop, whereToWriteInDM})
        }

        return fontSizeData
    }, {})
}

const getBuilderAlignment = (extensionAPI: ExtensionAPI, compPointer: CompRef): TextAlignStyleEntryItems => {
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    return builderComponents.getStyle(compPointer, false, [STYLE.CSS_PROPERTY_TYPE.textAlign]).map(styleData => ({
        data: styleData.value,
        prop: styleData.prop,
        whereToWriteInDM: styleData.whereToWriteInDM
    })) as TextAlignStyleEntryItems
}

const getBuilderData = (extensionAPI: ExtensionAPI, compPointer: Pointer): Record<string, any> => {
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const builderData = builderComponents.getData(compPointer, false).reduce((dataObj, {prop, value}) => {
        dataObj[prop] = value
        return dataObj
    }, {})
    const compType = components.getComponentType(compPointer)
    const dataType = builderTypes(schemaAPI, compType).data
    return schemaAPI.createItemAccordingToSchema(dataType, constants.DATA_TYPES.data, {
        data: builderData
    })
}

const builderCommonBuilder: ConversionDataBuilder = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
): Omit<ComponentConversionData, 'virtualParentId'> => {
    const component = dal.get(compPointer)
    const {variants} = extensionAPI as VariantsExtensionAPI

    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    const sourceLayout = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.layout)
    const targetLayout =
        conversionAlgoConfig.mode === AlgorithmModes.Adaptive
            ? variants.getComponentItemConsideringVariants(
                  pointers.components.getComponentVariantsPointer(compPointer, conversionAlgoConfig.targetVariants),
                  DATA_TYPES.layout
              )
            : undefined
    const transformations = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.transformations)
    const compArchType = builderComponents.getArchType(component.componentType)
    const fontSizeStyle = getBuilderFontSize(extensionAPI, compPointer)
    const measurement = variants.getComponentItemConsideringVariants(compPointer, DATA_TYPES.measurements)
    const compRect = _.pick(measurement, ['x', 'y', 'width', 'height']) as Rect

    const conversionData: Omit<ComponentConversionData, 'virtualParentId'> = {
        [ConversionDalNamespaces.structure]: {
            id: compPointer.id,
            componentType: component.componentType,
            archType: compArchType,
            semanticType: getSemanticType(compArchType, component.type),
            type: component.type!,
            skin: component.skin!,
            components: component.components,
            parent: component.parent!,
            axisAlignedEnvelope: createEnvelope(compRect, 0),
            data: getBuilderData(extensionAPI, compPointer)
        },
        [ConversionDalNamespaces.measurements]: compRect,
        [ConversionDalNamespaces.sourceLayout]: sourceLayout,
        [ConversionDalNamespaces.targetLayout]: targetLayout
    }
    if (fontSizeStyle.style) conversionData[ConversionDalNamespaces.style] = {fontSize: fontSizeStyle.style}
    if (fontSizeStyle.innerElementsStyle) {
        conversionData[ConversionDalNamespaces.innerElementsStyle] = {
            fontSize: fontSizeStyle.innerElementsStyle
        }
    }
    if (transformations?.rotate)
        conversionData[ConversionDalNamespaces.transformations] = {rotate: transformations.rotate}
    return conversionData
}

const removeFontSizeFromInlineCss = (htmlString: string) =>
    htmlString.replace(/style\s*=\s*"(.*?)"/gi, (match, inlineCssString: string) => {
        const newInlineCssString = inlineCssString.replace(/\s*font-size\s*:\s*[^;]+(;)?/gi, '').trim()

        return newInlineCssString ? `style=${newInlineCssString}` : ''
    })

const getFontSizeFromTheme = (extensionAPI: ExtensionAPI, fontThemeKeyFromData?: string) => {
    const fontThemes = (extensionAPI as ThemeExtAPI).theme.getTextTheme()

    if (!fontThemeKeyFromData || !fontThemes[fontThemeKeyFromData]) {
        return
    }

    const [fontSize] = /\d+/.exec(fontThemes[fontThemeKeyFromData].fontSize) ?? []

    if (!fontSize) {
        return
    }

    return parseFloat(fontSize)
}

const getUsedFontSizeForWRichText = (extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const dataItem = dataModel.components.getItem(compPointer, DATA_TYPES.data)
    const {text} = dataItem

    // hack for editor 2.0 (should be removed and not used in production)
    const [, inlineFontSize] = /font-size\s*:\s*([^;]+)\s*;/i.exec(text) ?? []

    if (inlineFontSize) {
        dataItem.text = removeFontSizeFromInlineCss(text)
        dataModel.addItem(dataItem, DATA_TYPES.data, pointers.structure.getPageOfComponent(compPointer).id, dataItem.id)
        const [fontSize] = /\d+/.exec(inlineFontSize.trim()) ?? []

        if (fontSize) {
            return parseFloat(fontSize)
        }
    }
    const [fontThemeKeyFromData] = /font_\d+/g.exec(text) ?? []

    return getFontSizeFromTheme(extensionAPI, fontThemeKeyFromData)
}

const getUsedFontFromSkinDefinition = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    compPointer: CompRef,
    pathToExtract: string
) => {
    const skinDefinition = getSkinDefinition(dal.get(compPointer).skin)
    const fontSizeFromSkin = _.get(skinDefinition, pathToExtract)
    const [fontThemeKeyFromData] = /font_\d+/.exec(fontSizeFromSkin) ?? []

    return getFontSizeFromTheme(extensionAPI, fontThemeKeyFromData)
}
const getMediaContainerUri = (extensionAPI: ExtensionAPI, compPointer: CompRef) => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const designData = dataModel.components.getItem(compPointer, DATA_TYPES.design)
    return designData?.background?.mediaRef?.uri
}
const getContainerAlignment = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef
): ComponentAlignment => {
    const children = pointers.structure.getChildren(compPointer)
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    return (
        children.reduce<ComponentAlignment | null>((alignment, pointer) => {
            const childStructure = dal.get(pointer)
            let rawAlignment
            const compType = (extensionAPI as StructureExtensionAPI).components.getComponentType(pointer)
            const type = (extensionAPI as StructureExtensionAPI).components.getType(pointer)
            if (builderComponents.isBuilder(compType)) {
                const semanticType = getSemanticType(builderComponents.getArchType(childStructure.componentType), type)
                if (alignment === CENTER || semanticType !== 'TEXT') {
                    return alignment
                }
                const style = (extensionAPI as BuilderComponentsExtensionAPI).builderComponents.getStyle(
                    pointer,
                    false,
                    [STYLE.CSS_PROPERTY_TYPE.textAlign]
                )
                if (_.isEmpty(style)) return alignment
                rawAlignment =
                    style.reduce<string | null>((finalAlignment, {value}) => {
                        const textAlignValue = value
                        if (!finalAlignment) return textAlignValue
                        if (finalAlignment !== textAlignValue) return CENTER
                        return finalAlignment
                    }, null) ?? LEFT
            } else {
                if (alignment === CENTER || childStructure.componentType !== 'wysiwyg.viewer.components.WRichText') {
                    return alignment
                }
                const baseStyleSettings = (
                    extensionAPI as VariantsExtensionAPI
                ).variants.getComponentItemConsideringVariants(pointer, 'style')
                rawAlignment = baseStyleSettings?.style?.properties?.textAlign ?? LEFT
            }
            const childAlignment = (rawAlignment === JUSTIFY ? undefined : rawAlignment) as ComponentAlignment
            if (childAlignment && !alignment) return childAlignment
            if (childAlignment && alignment !== childAlignment) return CENTER
            return alignment
        }, null) ?? CENTER
    )
}

const builderConversionDataBuilders: Record<string, PartialConversionDataBuilder> = {
    Container: (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        const alignment = getContainerAlignment(dal, extensionAPI, pointers, compPointer)
        return {
            [ConversionDalNamespaces.structure]: {
                alignment
            }
        }
    },
    TEXT: (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        return {
            [ConversionDalNamespaces.style]: {
                alignment: getBuilderAlignment(extensionAPI, compPointer)
            }
        }
    }
}

const customConversionDataBuilders: Record<string, PartialConversionDataBuilder> = {
    'wysiwyg.viewer.components.WRichText': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )
        const [componentFontSize] = /\d+/.exec(baseStyleSettings?.style?.properties?.fontSize) ?? []

        if (componentFontSize) {
            return {
                [ConversionDalNamespaces.style]: {
                    fontSize: [
                        {
                            prop: 'fontSize',
                            data: `${parseFloat(componentFontSize)}spx`,
                            whereToWriteInDM: 'cssProperties'
                        }
                    ],
                    alignment: [
                        {
                            prop: 'alignment',
                            data: baseStyleSettings?.style?.properties?.textAlign ?? LEFT,
                            whereToWriteInDM: 'cssProperties'
                        }
                    ]
                }
            }
        }
        const themeFontSize = getUsedFontSizeForWRichText(extensionAPI, pointers, compPointer)

        if (!themeFontSize) {
            return {}
        }

        return {
            [ConversionDalNamespaces.style]: {
                theme: {
                    fontSize: [
                        {
                            prop: 'fontSize',
                            data: `${themeFontSize}spx`,
                            whereToWriteInDM: 'cssProperties'
                        }
                    ]
                }
            }
        }
    },
    'wixui.StylableButton': (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )
        const [fontSize] = /\d+/.exec(baseStyleSettings?.style?.propertiesOverride?.font?.fontSize) ?? []

        if (!fontSize) {
            return {}
        }

        return {
            [ConversionDalNamespaces.style]: {
                fontSize: [
                    {
                        prop: 'stylableButton_fontSize',
                        data: `${parseFloat(fontSize)}spx`,
                        whereToWriteInDM: 'cssProperties'
                    }
                ]
            }
        }
    },
    'wysiwyg.viewer.components.SiteButton': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )
        const [fontSize] = /\d+/.exec(baseStyleSettings?.style?.propertiesOverride?.fnt?.fontSize) ?? []

        if (!fontSize) {
            return {}
        }

        return {
            [ConversionDalNamespaces.style]: {
                fontSize: [
                    {
                        prop: 'siteButton_fontSize',
                        data: `${parseFloat(fontSize)}spx`,
                        whereToWriteInDM: 'cssProperties'
                    }
                ]
            }
        }
    },
    'wixui.CollapsibleText': (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
        const baseStyleSettings = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
            compPointer,
            DATA_TYPES.theme
        )
        const skinFontSize = getUsedFontFromSkinDefinition(dal, extensionAPI, compPointer, 'font.defaultValue')
        const parsedStyle: StyleEntryItem = {
            theme: {
                fontSize: [
                    {
                        prop: 'collapsibleTextTheme_fontSize',
                        data: `${skinFontSize}spx`,
                        whereToWriteInDM: 'cssProperties'
                    }
                ],
                collapsibleTextButtonFontSize: skinFontSize
            }
        }
        const [componentFontSize] = /\d+/.exec(baseStyleSettings.style.propertiesOverride?.['text-font'].fontSize) ?? []
        const [componentCollapsibleTextButtonFontSize] =
            /\d+/.exec(baseStyleSettings.style.propertiesOverride?.['button-font'].fontSize) ?? []

        if (componentFontSize) {
            parsedStyle.fontSize = [
                {
                    prop: 'collapsibleText_fontSize',
                    data: `${parseFloat(componentFontSize)}spx`,
                    whereToWriteInDM: 'cssProperties'
                }
            ]
        }

        if (componentCollapsibleTextButtonFontSize) {
            parsedStyle.collapsibleTextButtonFontSize = parseFloat(componentCollapsibleTextButtonFontSize)
        }

        return {
            [ConversionDalNamespaces.style]: parsedStyle
        }
    },
    'mobile.core.components.Container': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const alignment = getContainerAlignment(dal, extensionAPI, pointers, compPointer)
        return {
            [ConversionDalNamespaces.structure]: {
                alignment
            }
        }
    },
    'responsive.components.Section': (
        dal: DAL,
        extensionAPI: ExtensionAPI,
        pointers: Pointers,
        compPointer: CompRef
    ) => {
        const alignment = getContainerAlignment(dal, extensionAPI, pointers, compPointer)
        return {
            [ConversionDalNamespaces.structure]: {
                alignment
            }
        }
    },
    ...Object.fromEntries(
        Array.from(MEDIA_CONTAINER_COMP_TYPE).map((compType: string) => [
            compType,
            (dal: DAL, extensionAPI: ExtensionAPI, pointers: Pointers, compPointer: CompRef) => {
                const uri = getMediaContainerUri(extensionAPI, compPointer)
                if (uri) {
                    return {
                        [ConversionDalNamespaces.structure]: {
                            media: {
                                uri
                            }
                        }
                    }
                }
            }
        ])
    )
}

export const getConversionData: ConversionDataBuilder = (
    dal: DAL,
    extensionAPI: ExtensionAPI,
    pointers: Pointers,
    compPointer: CompRef,
    conversionAlgoConfig: ConversionAlgoConfig
) => {
    const {builderComponents} = extensionAPI as BuilderComponentsExtensionAPI
    const componentType = (extensionAPI as StructureExtensionAPI).components.getComponentType(compPointer)
    const type = (extensionAPI as StructureExtensionAPI).components.getType(compPointer)
    const isBuilder = builderComponents.isBuilder(componentType)
    const conversionDataBuilders = isBuilder
        ? _.over(
              builderCommonBuilder,
              builderConversionDataBuilders[getSemanticType(builderComponents.getArchType(componentType), type)] ??
                  _.noop
          )
        : _.over(commonBuilder, customConversionDataBuilders[componentType] ?? _.noop)
    return _.merge(
        {},
        ...conversionDataBuilders(
            dal,
            extensionAPI,
            pointers,
            pointerUtils.getOriginalPointerFromInner(compPointer),
            conversionAlgoConfig
        )
    )
}
